import { GamoraUUIDGuard } from '@ver.id/libs.internal.gamora';
import { HoruzError } from '../../../../errors/error.js';
import { HoruzProtocolChulakMessageAppPairingEnterGuardInput } from '../../guards/message/app-pairing-enter.js';
import { HoruzProtocolChulakMessageAppTagsRefreshedGuardInput } from '../../guards/message/app-tags-refreshed.js';

/**
 * List of events
 * @public
 */
export enum HoruzProtocolChulakServiceStateEvent {
  Aborted = 'Aborted',
  Done = 'Done',
  Stopped = 'Stopped',
}

/**
 * List of invokers
 * @public
 */
export enum HoruzProtocolChulakServiceStateInvoker {
  RefreshAppTags = 'RefreshAppTags',
  CreateAppPairing = 'CreateAppPairing',
  CompletedAppPairing = 'CompletedAppPairing',
}

/**
 * @public
 */
export type HoruzProtocolChulakServiceStateInvokerRefreshAppTagsInput = {
  serviceSessionUuid: GamoraUUIDGuard;
};

/**
 * @public
 */
export type HoruzProtocolChulakServiceStateInvokerCompletedAppPairingInput = {
  serviceSessionUuid: GamoraUUIDGuard;
};

/**
 * @public
 */
export type HoruzProtocolChulakServiceStateInvokerCreateAppPairingInput = {
  serviceSessionUuid: GamoraUUIDGuard;
};

/**
 * @public
 */
export type HoruzProtocolChulakServiceStateEventAbortInput = {
  serviceSessionUuid: GamoraUUIDGuard;
  error?: HoruzError;
};

/**
 * @public
 */
export type HoruzProtocolChulakServiceStateEventDoneInput = {
  serviceSessionUuid: GamoraUUIDGuard;
};

/**
 * @public
 */
export type HoruzProtocolChulakServiceStateInvokerRefreshAppTagsResult =
  HoruzProtocolChulakMessageAppTagsRefreshedGuardInput;

/**
 * @public
 */
export type HoruzProtocolChulakServiceStateInvokerCreateAppPairingResult =
  HoruzProtocolChulakMessageAppPairingEnterGuardInput;

/**
 * List of yielders
 * @public
 *
 * Naming convention:
 * - Persist* = Single-pod operation (locks + writes to storage)
 * - Broadcast* = Multi-pod operation (sends to local WebSocket clients, no lock)
 */
export enum HoruzProtocolChulakServiceStateYielder {
  // Agent Start - Persist operations (single pod, with lock)
  PersistAgentStartMessage = 'PersistAgentStartMessage',
  PersistAgentStartRecoveryFlag = 'PersistAgentStartRecoveryFlag',
  // Agent Start - Broadcast operations (all pods, no lock)
  BroadcastAgentStartToClients = 'BroadcastAgentStartToClients',

  // App Presentation - Persist operations (single pod, with lock)
  PersistAppPresentationMessage = 'PersistAppPresentationMessage',
  PersistAppPresentationRecoveryFlag = 'PersistAppPresentationRecoveryFlag',
  // App Presentation - Broadcast operations (all pods, no lock)
  BroadcastAppPresentationToClients = 'BroadcastAppPresentationToClients',

  // Session Abort - Persist operations (single pod, with lock)
  PersistSessionAbortMessage = 'PersistSessionAbortMessage',
  PersistSessionAbortRecoveryFlag = 'PersistSessionAbortRecoveryFlag',
  // Session Abort - Broadcast operations (all pods, no lock)
  BroadcastSessionAbortToClients = 'BroadcastSessionAbortToClients',
}

/**
 * @public
 */
export interface HoruzProtocolChulakServiceStateYielderCompletedAppPresentationInput {
  compact: string;
}

/**
 * @public
 */
export type HoruzProtocolChulakServiceControllerAbortSessionInput = {
  error: HoruzError;
};
